/* eslint-disable react/jsx-no-constructed-context-values */
import React, {
    createContext,
    PropsWithChildren,
    useEffect,
    useState,
} from 'react';
import {
    Analytics,
    getAnalytics,
    initializeAnalytics,
    isSupported,
    logEvent,
    setUserProperties,
} from 'firebase/analytics';
import { FirebaseApp, getApps, initializeApp } from 'firebase/app';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { JourneyVariant } from '@r6digital/rapidstor-types/react/entities/Journey';
import Logger from '@/core/utils/logging';

const firebaseConfig = {
    apiKey: 'AIzaSyAWMv99uaZw6FnGtKFZ2LR8VlvlAopXx6w',
    authDomain: 'journey-based-listing.firebaseapp.com',
    projectId: 'journey-based-listing',
    storageBucket: 'journey-based-listing.appspot.com',
    messagingSenderId: '32814436608',
    appId: '1:32814436608:web:b45df054052ac9dcd5a3e5',
    measurementId: 'G-EP9K2MCB2Y',
};

// RapidStor is embedded on third-party websites that may run their own Firebase.
// Use a dedicated named app so we never collide with — or attach analytics to —
// the host's default app, which previously caused init errors and would have sent
// events to the wrong measurement ID.
const FIREBASE_APP_NAME = 'rapidstor';

export interface AnalyticsProps {
    firebaseApp: FirebaseApp | null;
    firebaseAnalytics: Analytics | null;
    firebaseLogEvent: (name: string, data: Record<string, string>) => void;
}

export const AnalyticsContext = createContext<AnalyticsProps>({
    firebaseApp: null,
    firebaseAnalytics: null,
    firebaseLogEvent: () => null,
});

export function AnalyticsContextProvider({
    location,
    children,
}: PropsWithChildren<{ location: Location }>) {
    const [firebaseApp, setFirebaseApp] = useState<FirebaseApp | null>(null);
    const [firebaseAnalytics, setFirebaseAnalytics] = useState<Analytics | null>(
        null,
    );
    const [isInitialized, setIsInitialized] = useState(false);

    const firebaseLogEvent = (name: string, data: Record<string, string>) => {
        if (!firebaseAnalytics || !firebaseApp) {
            if (!window.rapidstor_settings.rapidstor_options.configSelector) {
                return;
            }
            Logger.info('Firebase analytics not initialized', location);
            return;
        }

        logEvent(firebaseAnalytics, name, data);
    };

    const setupFirebaseInstance = (analytics: Analytics) => {
        setUserProperties(analytics, {
            location_code: location.locationCode,
            corporation_code: location.corpCode,
            location_name: location.name,
            variant: window.rapidstor_settings.app_config.journey.variant || JourneyVariant.JBL,
        });

        Logger.info('Firebase analytics setup complete');
    };

    useEffect(() => {
        if (window.rapidstor_settings.rapidstor_options.configSelector) {
            Logger.info(
                'Config selector or dev mode is enabled, skipping Firebase initialization',
            );
            return;
        }

        const initializeFirebase = async () => {
            try {
                const existing = getApps().find(
                    (existingApp) => existingApp.name === FIREBASE_APP_NAME,
                );
                const app = existing ?? initializeApp(firebaseConfig, FIREBASE_APP_NAME);
                setFirebaseApp(app);

                if (!(await isSupported())) {
                    Logger.info('Firebase analytics not supported in this environment, skipping');
                    setIsInitialized(true);
                    return;
                }

                // initializeAnalytics throws analytics/already-initialized if the
                // instance for this app was already created elsewhere (e.g. a lazy
                // default-options init on the host page or an earlier mount).
                // Attach to the existing instance instead of failing.
                let analytics: Analytics;
                try {
                    analytics = initializeAnalytics(app, {
                        config: {
                            send_page_view: true,
                        },
                    });
                } catch (initError) {
                    const isAlreadyInitialized = initError instanceof Error
                        && 'code' in initError
                        && initError.code === 'analytics/already-initialized';
                    if (!isAlreadyInitialized) {
                        throw initError;
                    }
                    analytics = getAnalytics(app);
                }
                setFirebaseAnalytics(analytics);
                setIsInitialized(true);
                Logger.info('Firebase initialized successfully');
            } catch (error) {
                const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
                Logger.error(`Error initializing Firebase — ${detail}`);
            }
        };

        if (!isInitialized) {
            initializeFirebase();
        }
    }, [isInitialized]);

    useEffect(() => {
        if (isInitialized && firebaseApp && firebaseAnalytics && location) {
            setupFirebaseInstance(firebaseAnalytics);
        }
    }, [isInitialized, firebaseApp, firebaseAnalytics, location]);

    return (
        <AnalyticsContext.Provider
            value={{
                firebaseApp,
                firebaseAnalytics,
                firebaseLogEvent,
            }}
        >
            {children}
        </AnalyticsContext.Provider>
    );
}
